import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, inr, MONTHS, formatErr } from "@/lib/api";

const Breakdown = ({ run }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/payroll", { params: { month: run.month, year: run.year } }).then((r) => setRows(r.data));
  }, [run.month, run.year]);
  if (!rows) return <Loader2 className="h-4 w-4 animate-spin m-3" />;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500"><th className="py-1">Staff</th><th>Days</th><th className="text-right">Deduction</th><th className="text-right">Allow.</th><th className="text-right">Net</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-t">
              <td className="py-1.5">{e.full_name}</td><td className="font-mono-num">{e.days_worked}/{e.days_in_month}</td>
              <td className="text-right font-mono-num text-rose-600">-{inr(e.leave_deductions)}</td>
              <td className="text-right font-mono-num">{inr(e.allowances)}</td>
              <td className="text-right font-mono-num font-semibold">{inr(e.final_payout)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PayrollApprovalPanel = () => {
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/payroll/runs").then((r) => setRuns(r.data.filter((x) => x.status === "pending_approval")));
  useEffect(() => {
    load();
  }, []);

  const approve = async (r) => {
    setBusy(`${r.year}-${r.month}`);
    try {
      const { data } = await api.post("/payroll/approve", { month: r.month, year: r.year });
      toast.success(`Approved · ${data.approved} payslips generated`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="bg-white rounded-2xl border p-5" data-testid="payroll-approval-panel">
      <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Payroll Approval</h2>
      {runs.length === 0 && <div className="text-sm text-slate-500 py-6 text-center" data-testid="no-pending-payroll">No payroll awaiting your approval.</div>}
      {runs.map((r) => {
        const key = `${r.year}-${r.month}`;
        return (
          <div key={key} className="rounded-xl border p-4 mb-3" data-testid={`pending-payroll-${key}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{MONTHS[r.month - 1]} {r.year}</div>
                <div className="text-xs text-slate-500">{r.count} employees · Total <span className="font-mono-num font-semibold text-slate-800">{inr(r.total)}</span></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(open === key ? null : key)} data-testid={`payroll-view-breakdown-${key}`}>
                  Breakdown<ChevronDown className="h-3 w-3 ml-1" />
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === key} onClick={() => approve(r)} data-testid="director-approve-payslips-button">
                  {busy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" />Approve &amp; Generate Payslips</>}
                </Button>
              </div>
            </div>
            {open === key && <Breakdown run={r} />}
          </div>
        );
      })}
    </section>
  );
};
